import { useMutation, useQueryClient } from "@tanstack/react-query";
import { XCircle } from "lucide-react";
import { useRef, useState, type DragEvent } from "react";
import { Link, useNavigate } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  Alert,
  Button,
  ErrorAlert,
  ErrorState,
  Form,
  FullPageSpinner,
  cx,
  Badge,
  ProgressBar,
  Breadcrumb,
  buttonClass,
} from "@/components/ui";
import { portal, runtime } from "@/lib/api";
import { ApiError } from "@/lib/http";
import { storageUsed, useHosting } from "@/lib/hosting";
import { useOverview } from "@/lib/queries";
import {
  checkSiteZip,
  MAX_BYTES,
  MAX_FILES,
  mb,
  siteNameFromFile,
  STAGE_LABEL,
  toStaticFiles,
  type CheckedSite,
  type CheckProgress,
} from "@/lib/static-site";
import { HostingIcon, HostingHead, textLink } from "@/components/hosting/parts";

const HOSTNAME = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** Where the deploy is: the site is made, then its files are sent, then the server takes them in. */
interface UploadState {
  phase: "creating" | "uploading" | "finishing";
  fraction: number;
}

/** Figma "Hosting — Add an external site" (HST-01). */
/** A refusal by the plan (the site limit here, the storage limit from the runtime) points to the plans. */
function DeployError({ error }: { error: unknown }) {
  const byPlan =
    error instanceof ApiError &&
    error.status === 409 &&
    (error.reason === "PLAN_LIMIT_REACHED" || error.reason === "QUOTA_EXCEEDED");
  if (!byPlan) return <ErrorAlert error={error} />;
  return (
    <Alert tone="warning">
      {error.message}{" "}
      <Link to="/billing/plans" className={textLink}>
        Compare plans →
      </Link>
    </Alert>
  );
}

export function AddExternalSitePage() {
  const { orgId, can } = useOrganization();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const overview = useOverview();
  const hosting = useHosting();
  const input = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState<CheckedSite | null>(null);
  const [checking, setChecking] = useState(false);
  const [progress, setProgress] = useState<CheckProgress | null>(null);
  const [upload, setUpload] = useState<UploadState | null>(null);
  /** Counts the files chosen, so a slow check of an earlier file cannot overwrite a later choice. */
  const latest = useRef(0);
  const [readError, setReadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [where, setWhere] = useState<"free" | "custom">("free");
  const [hostname, setHostname] = useState("");
  const uploadAbort = useRef<AbortController | null>(null);

  const deploy = useMutation({
    mutationFn: async (site: CheckedSite) => {
      // Never send a site that failed its checks (the button is disabled too; this is the last guard).
      const files = toStaticFiles(site);
      setUpload({ phase: "creating", fraction: 0 });
      const created = await portal.createSite(orgId, {
        name: siteNameFromFile(site.fileName),
        kind: "external",
      });
      try {
        if (where === "custom") await portal.addDomain(created.id, hostname.trim().toLowerCase());
        setUpload({ phase: "uploading", fraction: 0 });
        const controller = new AbortController();
        uploadAbort.current = controller;
        await runtime.deploy(
          created.id,
          files,
          (sent, total) =>
            setUpload({
              phase: sent >= total ? "finishing" : "uploading",
              fraction: total > 0 ? sent / total : 0,
            }),
          controller.signal,
        );
      } catch (err) {
        // Nothing went live: give the site slot back.
        await portal.deleteSite(created.id).catch(() => undefined);
        throw err;
      } finally {
        uploadAbort.current = null;
      }
      return created;
    },
    onSettled: () => setUpload(null),
    onSuccess: async (site) => {
      await qc.invalidateQueries({ queryKey: ["hosting"] });
      await qc.invalidateQueries({ queryKey: ["overview"] });
      void navigate(`/hosting/sites/${site.id}`);
    },
  });
  const cancelled = deploy.error instanceof DOMException && deploy.error.name === "AbortError";

  if (overview.isPending || hosting.isPending) return <FullPageSpinner />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  if (hosting.isError) return <ErrorState error={hosting.error} onRetry={() => void hosting.refetch()} />;

  const plan = overview.data.account.plan;
  const sites = hosting.data;
  const storageLeft = plan.storage_bytes - storageUsed(sites);
  const atLimit = sites.length >= plan.max_sites;
  const domainsLeft = plan.max_custom_domains - overview.data.usage.custom_domains;
  const hostnameOk = where === "free" || HOSTNAME.test(hostname.trim().toLowerCase());
  // Why "Deploy site" isn't available yet; a missing permission is explained by the button itself.
  const notReady = atLimit
    ? "Your plan's site limit is reached. Upgrade your plan to add another site."
    : checking
      ? "Checking your .zip…"
      : checked === null
        ? "Choose a .zip of your site first."
        : checked.problem !== null
          ? "Fix the problems above to continue."
          : !hostnameOk
            ? "Enter a valid domain name."
            : undefined;
  const ready = checked !== null && !notReady && can("websites:create");

  async function choose(file: File | undefined) {
    if (!file) return;
    const run = ++latest.current;
    setReadError(null);
    setChecked(null);
    if (!/\.zip$/i.test(file.name)) {
      setChecking(false);
      setProgress(null);
      setReadError(`“${file.name}” is not a .zip file. Put your site’s files in a .zip and choose that.`);
      return;
    }
    setChecking(true);
    setProgress({ stage: "reading", fraction: 0 });
    try {
      const result = await checkSiteZip(file, storageLeft, (p) => {
        if (run === latest.current) setProgress(p);
      });
      if (run === latest.current) setChecked(result);
    } catch (err) {
      if (run === latest.current)
        setReadError(err instanceof Error ? err.message : "That file could not be read.");
    } finally {
      if (run === latest.current) {
        setChecking(false);
        setProgress(null);
      }
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    void choose(e.dataTransfer.files[0]);
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: "Hosting", to: "/hosting" }, { label: "Add an external site" }]} />
      <HostingHead
        title="Add an external site"
        subtitle="Host a site you built somewhere else. We check it before anything goes live."
      />

      <Form
        error={deploy.error}
        validate={() => ({ hostname: !hostnameOk && "Enter a domain you own, like events.yourbusiness.com" })}
        className="flex w-full max-w-[760px] flex-col gap-[18px] rounded-[18px] border border-border bg-surface px-[22px] py-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) deploy.mutate(checked);
        }}
      >
        {atLimit && (
          <div className="rounded-xl bg-[#fce7c6] px-3.5 py-3 text-[12.5px] text-[#995c05]">
            Your {plan.name} plan hosts {plan.max_sites} {plan.max_sites === 1 ? "site" : "sites"}, and all
            are in use.{" "}
            <Link to="/billing/plans" className="font-semibold underline">
              Compare plans
            </Link>{" "}
            to host more.
          </div>
        )}

        <h2 className="text-[15px] font-bold text-fg">1. What kind of site is it?</h2>
        <div className="flex flex-col gap-2" role="radiogroup" aria-label="Kind of site">
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border-[1.5px] border-brand-500 bg-[#fef3f0] px-3.5 py-3">
            <input type="radio" name="kind" checked readOnly className="sr-only" />
            <HostingIcon name="radio-selected" size={18} />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-[13.5px] font-semibold text-fg">Static site</span>
              <span className="text-xs text-fg-subtle">
                HTML, CSS, JavaScript and images, e.g. exported from another builder
              </span>
            </span>
          </label>
          {["WordPress or PHP site", "Node.js, Python or other server apps"].map((label) => (
            <label
              key={label}
              className="flex items-center gap-3 rounded-xl border border-border bg-surface px-3.5 py-3 opacity-55"
            >
              <input type="radio" name="kind" disabled className="sr-only" />
              <span className="size-[18px] shrink-0 rounded-full border-[1.5px] border-[#d4cac4] bg-surface" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-[13.5px] font-semibold text-fg">{label}</span>
                <span className="text-xs text-fg-subtle">Not supported yet</span>
              </span>
              <HostingIcon name="lock" size={14} />
            </label>
          ))}
        </div>

        <div className="h-px bg-border" />
        <h2 className="text-[15px] font-bold text-fg">2. Upload your site</h2>
        <Button
          unstyled
          type="button"
          disabled={deploy.isPending}
          aria-busy={deploy.isPending}
          onClick={() => input.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cx(
            "flex w-full flex-col items-center gap-1.5 rounded-[14px] border-[1.5px] border-dashed py-[22px] disabled:cursor-not-allowed disabled:opacity-60",
            dragging
              ? "border-brand-500 bg-[#fef3f0]"
              : checked?.problem || readError
                ? "border-[#b8291f] bg-[#fdf1ef]"
                : "border-border bg-brand-50 hover:border-[#d4cac4]",
          )}
        >
          <HostingIcon name="upload" size={22} />
          <span className="text-[13.5px] font-semibold text-fg">
            {checking
              ? "Checking your files…"
              : checked || readError
                ? "Choose a different .zip file"
                : "Drop a .zip file here or choose a file"}
          </span>
          <span className="text-xs text-fg-subtle">
            Up to {MAX_BYTES / 2 ** 20} MB once unpacked and {MAX_FILES.toLocaleString()} files · must include
            an index.html at the top level
          </span>
        </Button>
        <input
          ref={input}
          type="file"
          accept=".zip,application/zip"
          className="hidden"
          onChange={(e) => {
            void choose(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        {progress && <ProgressBar label={STAGE_LABEL[progress.stage]} fraction={progress.fraction} />}
        {readError && <Alert tone="danger">{readError}</Alert>}

        {checked && (
          <div className="flex flex-col gap-2.5 rounded-xl border border-border bg-surface px-4 py-3.5">
            <div className="flex items-center gap-2.5">
              <HostingIcon name="database-file" size={18} />
              <div className="flex min-w-0 flex-1 flex-col gap-px">
                <span className="truncate text-[13.5px] font-semibold text-fg">{checked.fileName}</span>
                <span className="text-xs text-fg-subtle">
                  {checked.fileCount > 0
                    ? `${mb(checked.zipBytes)} .zip · ${mb(checked.totalBytes)} once unpacked · ${checked.fileCount.toLocaleString()} files`
                    : `${mb(checked.zipBytes)} .zip`}
                </span>
              </div>
              {checked.problem ? (
                <Badge tone="danger">Needs changes</Badge>
              ) : (
                <Badge tone="success">Checks passed</Badge>
              )}
            </div>
            {checked.checks.map((c) => (
              <div key={c.label} className="flex items-center gap-2">
                {c.ok ? (
                  <HostingIcon name="check-circle-sm" size={13} />
                ) : (
                  <XCircle className="size-[13px] shrink-0 text-[#b8291f]" aria-hidden />
                )}
                <span className={cx("text-[12.5px]", c.ok ? "text-fg-muted" : "text-[#b8291f]")}>
                  {c.label}
                </span>
              </div>
            ))}
            {checked.problem && (
              <Alert tone="danger">
                <p className="font-semibold">
                  This site can’t be deployed yet, and nothing has been uploaded.
                </p>
                <p>Fix what is marked in red, make the .zip again, then choose it here.</p>
              </Alert>
            )}
            {checked.hints.length > 0 && (
              <Alert tone="warning">
                <ul className="flex list-disc flex-col gap-1 pl-4">
                  {checked.hints.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </div>
        )}

        <div className="h-px bg-border" />
        <h2 className="text-[15px] font-bold text-fg">3. Where should it live?</h2>
        <div className="relative flex items-center">
          <HostingIcon name="globe" size={15} className="pointer-events-none absolute left-[15px]" />
          <select
            aria-label="Address"
            value={where}
            onChange={(e) => setWhere(e.target.value as "free" | "custom")}
            className="w-full appearance-none rounded-xl border border-border bg-surface py-[11px] pr-10 pl-[38px] text-[13.5px] text-fg outline-none focus:border-brand-500"
          >
            <option value="free">A free CortexGrip address</option>
            <option value="custom" disabled={domainsLeft <= 0}>
              A domain I own{domainsLeft <= 0 ? ` (not included on ${plan.name})` : ""}
            </option>
          </select>
          <HostingIcon name="chevron-down" size={14} className="pointer-events-none absolute right-[15px]" />
        </div>
        {where === "custom" && (
          <input
            aria-label="Domain"
            name="hostname"
            required
            placeholder="events.yourbusiness.com"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            className={cx(
              "w-full rounded-xl border bg-surface px-3.5 py-[11px] text-[13.5px] text-fg outline-none focus:border-brand-500",
              hostname && !hostnameOk ? "border-[#b8291f]" : "border-border",
            )}
          />
        )}
        <p className="text-xs leading-normal text-fg-subtle">
          {where === "custom"
            ? "After you deploy, we show the DNS record to add at your domain provider. The security certificate is issued after DNS is confirmed."
            : "It is served on its own CortexGrip address with a security certificate as soon as it is deployed. You can connect a domain later."}
          {domainsLeft <= 0 && where === "free" && (
            <>
              {" "}
              <Link to="/billing/plans" className={textLink}>
                Compare plans →
              </Link>
            </>
          )}
        </p>

        {cancelled ? (
          <Alert tone="warning">Upload cancelled. Nothing went live.</Alert>
        ) : (
          deploy.error && <DeployError error={deploy.error} />
        )}
        {deploy.isPending && upload && (
          <div className="flex flex-col gap-2">
            <p className="sr-only" aria-live="polite">
              {upload.phase === "creating"
                ? "Creating your site…"
                : upload.phase === "uploading"
                  ? "Uploading your files…"
                  : "Upload complete. Finishing up…"}
            </p>
            <ProgressBar
              label={
                upload.phase === "creating"
                  ? "Creating your site…"
                  : upload.phase === "uploading"
                    ? "Uploading your files…"
                    : "Upload complete. Finishing up…"
              }
              fraction={upload.phase === "uploading" ? upload.fraction : null}
              detail="Keep this page open until it finishes."
            />
            {upload.phase === "uploading" && (
              <Button
                unstyled
                type="button"
                onClick={() => uploadAbort.current?.abort()}
                className={cx(buttonClass("secondary", "pill"), "self-start")}
              >
                Cancel upload
              </Button>
            )}
          </div>
        )}
        <div className="flex items-center justify-end gap-2.5">
          {checked?.problem && (
            <span className="mr-auto text-xs text-[#b8291f]">Fix the problems above to continue.</span>
          )}
          <Link to="/hosting" className={buttonClass("secondary", "pill")}>
            Cancel
          </Link>
          <Button
            type="submit"
            variant="brand"
            size="pill"
            requires="websites:create"
            loading={deploy.isPending}
            disabledReason={notReady}
          >
            {deploy.isPending ? "Deploying…" : "Deploy site"}
          </Button>
        </div>
      </Form>
    </div>
  );
}
